import type { ComponentsArticleProductCard } from 'queries/graphql';
import Link from 'next/dist/client/link';
import dynamic from 'next/dynamic';
import Image from 'next/image';

import { buildCssVar } from 'utils/style-override';
import { buildImgProps } from 'utils/media';
import { useTextFontOverrides, useHeadingFontOverrides } from 'utils/font-override';

import { buildProductUrl } from 'utils/article-helpers';

import styles from './styles.module.scss';

const MarkdownText = dynamic(() => import('generic/MarkdownText'));

export default function ProductCard(props: ComponentsArticleProductCard) {
  const { sectionId, title, description, image, buttonText, productPrice, productName, style } =
    props;

  const {
    titleColor,
    textColor,
    buttonColor,
    buttonTextColor,
    buttonBorderColor,
    buttonOnHoverColor,
    buttonOnHoverTextColor,
    buttonOnHoverBorderColor,
    textFont,
    headingFont,
  } = style;

  const ctaUrl = buildProductUrl(props);

  const textFontOverrides = useTextFontOverrides(textFont);
  const headingFontOverrides = useHeadingFontOverrides(headingFont);

  return (
    <div id={sectionId} className={`root ${styles.container} ${styles['layout1']}`}>
      <style jsx>{`
        .root {
          ${buildCssVar('--text-color', textColor, 'var(--text-color-dark)')}
          ${buildCssVar('--heading-color', titleColor, 'var(--heading-color-dark)')}
          ${buildCssVar('--button-color', buttonColor, '#121212')}
          ${buildCssVar('--button-text-color', buttonTextColor, 'var(--text-color-light)')}
          ${buildCssVar('--button-border-color', buttonBorderColor, '#121212')}
          ${buildCssVar('--button-on-hover-color', buttonOnHoverColor, 'transparent')}
          ${buildCssVar(
            '--button-on-hover-text-color',
            buttonOnHoverTextColor,
            'var(--text-color-dark)'
          )}
          ${buildCssVar('--button-on-hover-border-color', buttonOnHoverBorderColor, '#121212')}

          ${headingFontOverrides ?? ''}
          ${textFontOverrides ?? ''}
        }
      `}</style>

      <h1 className={styles.heading}>{title}</h1>
      <div className={styles.img_name_wrapper}>
        <div className={styles.img_wrapper}>
          {image && <Image {...buildImgProps(image)} alt={image.alt} />}
        </div>

        {productName && productPrice && (
          <div className={styles.price_product_wrapper}>
            <p className={styles.product}>{productName}</p>
            <p className={styles.price}>${productPrice}</p>
            {ctaUrl && (
              <Link href={ctaUrl} legacyBehavior>
                <a target="_blank">
                  <div className={styles.btn}>{buttonText || 'Read More'}</div>
                </a>
              </Link>
            )}
          </div>
        )}
      </div>

      <div className={styles.text_wrapper}>
        {description && <MarkdownText text={description} />}
      </div>

      <div className={styles.btn_wrapper}>
        {ctaUrl && (
          <Link href={ctaUrl} legacyBehavior>
            <a target="_blank">
              <div className={styles.btn}>{buttonText || 'Read More'}</div>
            </a>
          </Link>
        )}
      </div>
    </div>
  );
}
